import { getOfferValuesApi } from "api";
import { OFFER_INIT_INFO, ALL_COUPON_TYPES_PREDICTIVE_TEXT } from "config";
import { extractTextBetweenFirstBalancedParenthesis } from 'utils';
import _ from "lodash";

// --- Coupon auto-flagging (PEP-6516) -----------------------------------------
const NON_COUPON_DROPDOWN_VALUES = [
  "promo code",
  "rebate",
  "digital rebate",
  "mail-in rebate",
  "text rebate",
  "instant rebate",
];
const COUPON_DROPDOWN_VALUES = ALL_COUPON_TYPES_PREDICTIVE_TEXT
  .map((c) => `${c?.id || ""}`.trim().toLowerCase())
  .filter((v) => v && !NON_COUPON_DROPDOWN_VALUES.includes(v));

const adblockHasCouponValue = (offers = [], incentives = []) => {
  const texts = [
    ...(offers || []).map((o) => `${o?.offer_text || ""}`.toLowerCase()),
    ...(incentives || [])
      .filter((i) => i?.incentive_attach_to === "Adblock")
      .map((i) => `${i?.incentive_text || ""}`.toLowerCase()),
  ];
  return texts.some((t) => t && COUPON_DROPDOWN_VALUES.some((v) => t.includes(v)));
};

export const getSaveOfferCouponAmount = (offers = []) => {
  const saveAmountRegex = /^save\s+\$([\d,]+(?:\.\d+)?)/i;
  const saveOffers = (offers || []).filter((o) => {
    if ((o?.offer_type || "").toLowerCase() !== "save") return false;
    const text = `${o?.offer_text || ""}`.toLowerCase();
    return COUPON_DROPDOWN_VALUES.some((v) => text.includes(v));
  });
  const ordered = [
    ...saveOffers.filter((o) => o.include_in_net_cost_f),
    ...saveOffers.filter((o) => !o.include_in_net_cost_f),
  ];
  for (const offer of ordered) {
    const text = `${offer?.offer_text || ""}`.replace(/an extra|up to/i, "").trim();
    const match = text.match(saveAmountRegex);
    if (match && match[1]) {
      return match[1].replace(/,/g, "");
    }
  }
  return null;
};

// Computes the Coupon box state for an adblock:
export const computeCouponFlags = ({ offers = [], incentives = [], couponF = false, couponContext = false } = {}) => {
  const hasCouponValue = adblockHasCouponValue(offers, incentives);

  const couponDisabled = !(hasCouponValue || !!couponContext);

  let coupon_f;
  if (hasCouponValue) {
    coupon_f = true;
  } else if (couponDisabled) {
    coupon_f = false;
  } else {
    coupon_f = !!couponF;
  }

  const coupon_amnt = coupon_f ? getSaveOfferCouponAmount(offers) : null;

  return { coupon_f, coupon_amnt, couponDisabled };
};

export const normalizeWPurchaseText = (text) => {
  if (typeof text !== "string") return text;

  return text
    .replace(/\/\s+/g, "/")
    .replace(/\bw\/purchase\s+(?=w\/)/gi, "w/purchase/")
    .replace(/\bw\/purchase\s+(?=(?:\d|\$|any\b))/gi, "w/purchase/");
};


export const updateOffersBasedOnDigitalCoupon = (offersData) => {
  let isSaveOffer = false;
  let offerDataObj;
  if (offersData.length) {
    const updatedOffers = offersData.map((offer) => {
      if (offer.offer_type.toLowerCase() === 'save') {
        isSaveOffer = true;
        let offerText = offer.existingOffer || offer.offer_text.includes('w/digital coupon') ? offer.offer_text : offer.offer_text + ' w/digital coupon';
        if (!offerText.includes('save ')) {
          offerText = 'save ' + offerText;
        }
        const updatedOffer = {
          ...offer,
          offer_text: offerText,
          offer_type: 'Save'
        };
        // isValid computed on the original raw text is stale.
        updatedOffer.isValid = validateOffer(updatedOffer);
        return updatedOffer;
      }
      return offer;
    });
    if (isSaveOffer) {
      return updatedOffers;
    } else {
      offerDataObj = {
        ...OFFER_INIT_INFO,
        index: offersData.length,
        isValid: true,
        offer_type: "Other",
        include_in_net_cost_f: true,
        offer_text: 'w/digital coupon'
      };
      return [...updatedOffers, offerDataObj];
    }
  } else {
    offerDataObj = {
      ...OFFER_INIT_INFO,
      index: 0,
      isValid: true,
      offer_type: "Other",
      include_in_net_cost_f: true,
      offer_text: 'w/digital coupon'
    };
    return [offerDataObj];
  }
};

export function processOffers(offers) {
  const minPriceEa = offers.filter(o => o.offer_type?.toLowerCase() === 'price ea')[0];
  console.log("minPriceEa:", minPriceEa);
  let pricePoint = 0;
  if (minPriceEa) {
    // Check if there is a 'Price ea' offer and calculate the price point
    if (minPriceEa && parseFloat(minPriceEa.value_1)) {
      if (parseFloat(minPriceEa.value_2)) {
        pricePoint = Math.min(
          parseFloat(minPriceEa.value_1),
          parseFloat(minPriceEa.value_2)
        );
      } else {
        pricePoint = parseFloat(minPriceEa.value_1);
      }
      console.log("Price Point from minPriceEa:", pricePoint, parseFloat(minPriceEa.value_1));
      console.log("Calculated pricePoint:", pricePoint);
    }
  }

  if (!offers || offers.length === 0) {
    // No offers available, return the calculated price point
    console.log("No offers available. Returning pricePoint:", pricePoint);
    return pricePoint;
  }

  const minXFor = offers
    .filter(o => o.offer_type?.toLowerCase() === 'x for' && o.unit_price && parseFloat(o.unit_price) > 0)
    .sort((a, b) => parseFloat(a.unit_price) - parseFloat(b.unit_price))[0];

  const maxBOGO = offers
    .filter(o => o.offer_type?.toLowerCase() === 'bogo')
    .sort((a, b) => parseFloat(b.value_1) - parseFloat(a.value_1))[0];

  const saveOffers = offers.filter(o => o.offer_type?.toLowerCase() === 'save');

  // Remove 'Price ea' offers from the list
  offers = offers.filter(o => o.offer_type?.toLowerCase() !== 'price ea');
  console.log("Filtered offers:", offers);

  if (minXFor && maxBOGO) {
    // Both 'X For' and 'BOGO' offers exist
    if (parseFloat(minXFor.value_1) === parseFloat(maxBOGO.value_1)) {
      // 'X For' and 'BOGO' have the same value_1
      switch (parseInt(maxBOGO.dd_index)) {
        case 0: // Percentage discount
          const result0 = (
            (parseFloat(minXFor.value_2) +
              parseFloat(minXFor.unit_price) *
              (maxBOGO.offer_text.includes("off")
                ? (100 - parseFloat(maxBOGO.value_3)) / 100
                : parseFloat(maxBOGO.value_3) / 100)) /
            (parseFloat(maxBOGO.value_1) + parseFloat(maxBOGO.value_2))
          );
          console.log("Returning result for case 0 (%):", result0);
          return result0;
        case 1: // Dollar discount
          const result1 = maxBOGO.offer_text.includes("off")
            ? parseFloat(minXFor.unit_price) -
            parseFloat(maxBOGO.value_3) /
            (parseFloat(maxBOGO.value_1) + parseFloat(maxBOGO.value_2))
            : (parseFloat(maxBOGO.value_1) + parseFloat(maxBOGO.value_2)) /
            (parseFloat(minXFor.value_2) + parseFloat(maxBOGO.value_3));
          console.log("Returning result for case 1 ($):", result1);
          return result1;
        case 2: // Free offer
          const result2 = parseFloat(minXFor.value_2) /
            (parseFloat(minXFor.value_1) + parseFloat(maxBOGO.value_2));
          console.log("Returning result for case 2 (Free):", result2);
          return result2;
      }
    } else {
      // 'X For' and 'BOGO' have different value_1
      if (parseInt(maxBOGO.dd_index) === 2) {
        const result3 = parseFloat(minXFor.value_2) /
          ((parseFloat(minXFor.value_1) / parseFloat(maxBOGO.value_1)) *
            parseFloat(maxBOGO.value_2) +
            parseFloat(minXFor.value_1));
        console.log("Returning result for mismatched XFor and BOGO (case 2):", result3);
        return result3;
      }
    }
  } else if (
    offers.filter(o => o.offer_type?.toLowerCase() !== 'bogo').length === 0 &&
    maxBOGO &&
    pricePoint
  ) {
    // Only 'BOGO' offers exist with a price point
    if (pricePoint > 0) {
      switch (parseInt(maxBOGO.dd_index)) {
        case 0: // Percentage discount
          const result4 = (
            (pricePoint * parseFloat(maxBOGO.value_1) +
              parseFloat(maxBOGO.value_2) *
              pricePoint *
              (maxBOGO.offer_text.includes("off")
                ? (100 - parseFloat(maxBOGO.value_3)) / 100
                : parseFloat(maxBOGO.value_3) / 100)) /
            (parseFloat(maxBOGO.value_1) + parseFloat(maxBOGO.value_2))
          );
          console.log("Returning result for case 0 (%):", result4);
          return result4;
        case 1: // Dollar discount
          const result5 = maxBOGO.offer_text.includes("off")
            ? (pricePoint * parseFloat(maxBOGO.value_1) +
              parseFloat(maxBOGO.value_2) *
              (pricePoint - parseFloat(maxBOGO.value_3))) /
            (parseFloat(maxBOGO.value_1) + parseFloat(maxBOGO.value_2))
            : (pricePoint * parseFloat(maxBOGO.value_1) +
              parseFloat(maxBOGO.value_2) * parseFloat(maxBOGO.value_3)) /
            (parseFloat(maxBOGO.value_1) + parseFloat(maxBOGO.value_2));
          console.log("Returning result for case 1 ($):", result5);
          return result5;
        case 2: // Free offer
          const result6 = (parseFloat(maxBOGO.value_1) * pricePoint) /
            (parseFloat(maxBOGO.value_1) + parseFloat(maxBOGO.value_2));
          console.log("Returning result for case 2 (Free):", result6);
          return result6;
      }
    }
  } else if (minXFor && saveOffers.length > 0) {
    // Both 'X For' and 'Save' offers exist
    let result = parseFloat(minXFor.unit_price);
    saveOffers.forEach(saveOffer => {
      console.log(
        saveOffer.dd_index,
        saveOffer.min_qty,
        saveOffer.qty_off_total_purchase_f,
        saveOffer.min_qty_dollar_f
      );
      switch (parseInt(saveOffer.dd_index)) {
        case 0: // Percentage discount
          result -=
            result *
            (saveOffer.value_2 && parseFloat(saveOffer.value_2)
              ? parseFloat(saveOffer.value_2)
              : parseFloat(saveOffer.value_1) / 100);
          console.log("Updated result for Save offer (%):", result);
          break;
        case 1: // Dollar discount
          if (
            parseFloat(saveOffer.min_qty) &&
            parseInt(saveOffer.min_qty) > 0 &&
            saveOffer.qty_off_total_purchase_f
          ) {
            if (saveOffer.min_qty_dollar_f) {
              result =
                (parseFloat(saveOffer.min_qty) -
                  parseFloat(saveOffer.value_1)) /
                ((parseFloat(saveOffer.min_qty) /
                  parseFloat(minXFor.value_2)) *
                  parseFloat(minXFor.value_1));
            } else {
              result -=
                (saveOffer.value_2 && parseFloat(saveOffer.value_2)
                  ? parseFloat(saveOffer.value_2)
                  : parseFloat(saveOffer.value_1)) /
                parseFloat(saveOffer.min_qty);
            }
          } else if (!saveOffer.qty_off_total_purchase_f) {
            result -= saveOffer.value_2 && parseFloat(saveOffer.value_2)
              ? parseFloat(saveOffer.value_2)
              : parseFloat(saveOffer.value_1);
          } else {
            result -=
              (saveOffer.value_2 && parseFloat(saveOffer.value_2)
                ? parseFloat(saveOffer.value_2)
                : parseFloat(saveOffer.value_1)) /
              parseFloat(minXFor.value_1);
          }
          console.log("Updated result for Save offer ($): 467", result);
          break;
      }
    });
    console.log("Returning result for XFor and Save offers:", result);
    return result;
  } else if (pricePoint && pricePoint > 0 && saveOffers.length > 0) {
    // 'Save' offers exist with a price point
    console.log("Save offers with price point exist.");
    let result = pricePoint;
    saveOffers.forEach(saveOffer => {
      console.log(saveOffers)
      console.log(pricePoint, saveOffer.dd_index, saveOffer.min_qty, saveOffer.qty_off_total_purchase_f, saveOffer.min_qty_dollar_f);
      const dd_index = parseInt(saveOffer.dd_index);
      if (dd_index === 0) {
        result -= result * (parseFloat(saveOffer.value_2) ?
          parseFloat(saveOffer.value_2) :
          parseFloat(saveOffer.value_1) / 100);
        console.log("Save off with price point exist - dd_index: 0", result);
      }
      else if (dd_index === 1) {
        if (
          parseInt(saveOffer.min_qty) &&
          parseInt(saveOffer.min_qty) > 0 &&
          saveOffer.qty_off_total_purchase_f
        ) {
          if (saveOffer.min_qty_dollar_f) {
            const totalUnitsPurchased = Math.ceil(
              parseFloat(saveOffer.min_qty) / pricePoint
            );
            result =
              ((totalUnitsPurchased * pricePoint) -
                parseFloat(saveOffer.value_1)) /
              totalUnitsPurchased;
          } else {
            result -= (parseFloat(saveOffer.value_2) ?
              parseFloat(saveOffer.value_2) :
              parseFloat(saveOffer.value_1)) /
              parseFloat(saveOffer.min_qty);
          }
        } else {
          result -= (parseFloat(saveOffer.value_2) ?
            parseFloat(saveOffer.value_2) :
            parseFloat(saveOffer.value_1));
        }
      }
    });
    console.log("Returning result for pricePoint and Save offers:", result);
    return result;
  } else if (minXFor) {
    // Only 'X For' offers exist
    console.log("Returning unit price for XFor offer:", parseFloat(minXFor.unit_price));
    return parseFloat(minXFor.unit_price);
  } else if (offers.length === 1) {
    // Single offer exists
    const singleOffer = offers[0];
    const result = singleOffer.unit_price && parseFloat(singleOffer.unit_price) > 0
      ? parseFloat(singleOffer.unit_price)
      : pricePoint;
    console.log("Returning result for single offer:", result);
    return result;
  }

  // Default return case
  console.log("Default return. Returning pricePoint:", pricePoint);
  return pricePoint;
}

export function isValidPriceEAOffers(offerData, isLinerDisabled, split) {

  let is_valid = offerData.every(function (e) {
    return e?.offer_type === "Price ea" ? (e.reg_price ? (parseFloat(e.reg_price?.toString().replace(/[^0-9\.]+/g, "")) >= parseFloat(e.offer_text.split('-')[0]?.toString().replace(/[^0-9\.]+/g, ""))) : true) : e?.isValid;
  })

  if (is_valid) {
    const priceEaOffers = offerData.filter(
      (e) => e?.offer_type === "Price ea"
    );
    if (
      priceEaOffers.length > 1 &&
      isLinerDisabled &&
      !split
    ) {
      return false;
    }
    else {
      return true;
    }
  }
  return false;
}

// This follows useEffect logic from OfferInfo component
export const checkMinQtyDollarF = (offerText, offerType) => {
  if (offerText && (offerType.toLowerCase() === 'save' || offerType.toLowerCase() === 'x for')) {
    const wPurchaseMatch = offerText.match(/w\/purchase\/(?:any\s)?(\$\d+(\.\d+)?|\d+)/i);
    if (wPurchaseMatch) {
      return {
        min_qty: wPurchaseMatch[1].replace('$', '') || "",
        min_qty_dollar_f: wPurchaseMatch[1].includes('$') || false,
      };
    }
  }
  return {
    min_qty: "",
    min_qty_dollar_f: false,
  };
}

export const offerStringEval = (offer = {}, index = 0, displayOnly = false) => {
  const offet_text = offer.offer_text;
  const liner = offer.liner_f ? " - Liner" : "";
  const offer_type = offer.offer_type;
  let extra = '';
  if (displayOnly) {
    extra = (offer?.limit || offer?.reg_price || offer?.reg_price2_d) ? ' | ' : '';
    extra += [
      offer?.limit && `Limit: ${offer.limit}`,
      offer?.reg_price && `RP Start: ${offer.reg_price}`,
      offer?.reg_price2_d && `RP End: ${offer.reg_price2_d}`,
    ]
      .filter(Boolean)
      .join(' | ');
  }

  return (
    <div className="flex-1">
      {index + 1}. {offer_type ? `${offer_type} - ` : ""} {offet_text} {liner} {extra}
    </div>
  );
};

export const validateIncentive = (incentive) => {
  return Boolean(incentive?.incentive_type && incentive?.incentive_text);
};

export const validateOffer = (offer) => {
  if (offer?.offer_type?.toLowerCase().startsWith('x')) { // XFor restrictions
    if (!offer?.offer_text?.includes('/')) {
      return false;
    }
  }
  // validate w/purchase product - it must have product/s after w/purchase/ in offer_text
  if (offer?.offer_type?.toLowerCase() === 'w/purchase product') {
    const purchaseProductRegex = /w\/purchase\/((?=[a-z0-9\s]*[a-z])[a-z0-9\s]+)/i;
    const match = offer?.offer_text?.toLowerCase().match(purchaseProductRegex);
    if (!match || !match[1] || match[1].trim() === '') {
      return false;
    }
  }

  // Validate w/purchase/$ must have a dollar value in offer_text
  if (offer?.offer_type?.toLowerCase() === 'w/purchase/$') {
    const purchaseDollarRegex = /w\/purchase\/\$(\d+(?:\.\d+)?)/i;
    const match = offer?.offer_text?.toLowerCase().match(purchaseDollarRegex);
    console.log('Validating w/purchase/$ offer:', offer?.offer_text, match);
    if (match) {
      const purchaseAmount = Number(match[1]);
      if (Number.isNaN(purchaseAmount) || purchaseAmount <= 0) {
        return false;
      }
    } else {
      return false;
    }
  }

  if (offer?.offer_type?.toLowerCase().startsWith('w/purchase/qty')) {
    const purchaseQtyRegex = /w\/purchase\/(\d+)/i;
    const match = offer?.offer_text?.toLowerCase().match(purchaseQtyRegex);
    if (match) {
      const purchaseQty = Number(match[1]);
      if (Number.isNaN(purchaseQty) || purchaseQty <= 0) {
        return false;
      }
    } else {
      return false;
    }
  }

  if (offer?.offer_type?.toLowerCase() === 'bogo') {
    const bogoFreeRegex = /B\d+G\d+F/i;
    const match = offer?.offer_text?.toLowerCase().match(bogoFreeRegex);
    if (!match) {
      const bogoValueRegex = /B\d+G\d+\s\$\d+|B\d+G\d+\s\d+%/i;
      const valueMatch = offer?.offer_text?.toLowerCase().match(bogoValueRegex);
      if(!valueMatch) {
        return false;
      }
    }
  }
  
  if (offer?.offer_type?.toLowerCase() === 'save') {
    if (!offer?.offer_text?.toLowerCase().startsWith('save')) {
      return false;
    }
    const text = offer?.offer_text?.toLowerCase() || '';
    const requiresPurchase = text.includes('w/purchase/');
    const purchaseWithDigitOnlyRegex = /w\/purchase\/(?!any)(\$?\d+(?:\.\d+)?)/i;
    const purchaseWithNameRegex = /w\/purchase\/(?!any)[a-zA-Z]+/i;
    const purchaseAnyWithDigitRegex = /w\/purchase\/any\s+(\$?\d+(?:\.\d+)?)/i;

    // Strictly disallow w/purchase{digit} (no slash)
    if (/w\/purchase\d+/i.test(text)) {
      return false;
    }

    // Disallow a trailing "w/purchase/" with no text following it
    // e.g. "save $4.00 w/purchase/" is invalid
    if (/w\/purchase\/\s*$/i.test(text)) {
      return false;
    }

    if (!requiresPurchase) {
      // // Need to invalidate if 'any' is selected without following digit or $ amount(regarless of w/purchase)
      // if (/\bany\b/.test(text) && !/\bany\s+(\$?\d+(?:\.\d+)?)\b/.test(text)) {
      //   return false;
      // }
      return true;
    }

    // If w/purchase/any is present, it must be followed by a digit
    if (/\bw\/purchase\/any\b/.test(text)) {
      if (purchaseAnyWithDigitRegex.test(text)) return true;
      return false;
    }

    // Only allow w/purchase/{digit} or w/purchase/{name} if not w/purchase/any
    const hasPurchaseWithDigit = purchaseWithDigitOnlyRegex.test(text);
    const hasPurchaseWithName = purchaseWithNameRegex.test(text);
    
    // // validate if w/purchase is selected, it must be followed by a digit or $ amount to valid offer.
    // if(requiresPurchase && !hasPurchaseWithDigit) {
    //   return false;
    // }

    if (hasPurchaseWithDigit) {
      // Ensure that the digit after w/purchase/ matches offer.min_qty
      const match = text.match(purchaseWithDigitOnlyRegex);
      if (match && Number(match[1].replace('$', '')) === Number(offer.min_qty)) {
        return true;
      }
      return false;
    } else if (hasPurchaseWithName) {
      return true;
    } else {
      return true;
    }
  }

  if (offer?.offer_type?.toLowerCase() === 'price ea') {
    const priceValues = offer.offer_text?.split("-") || [];
    const hasValidCurrencyFormat =
      priceValues.length > 0 &&
      priceValues.length <= 2 &&
      priceValues.every((price) =>
        /^\s*\$\s*(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?\s*$/.test(price)
      );
    return hasValidCurrencyFormat
  }
  return Boolean(offer?.offer_type && offer?.offer_text);
};

// Mirrors the backend _offer_key (app/utils/ad.py) plus liner_f, which the
// backend uses as a separate grouping when de-duplicating offers.
const offerEntryKey = (offer = {}) =>
  JSON.stringify({
    offer_type: offer.offer_type,
    offer_text: offer.offer_text,
    dd_index: offer.dd_index,
    include_in_net_cost_f: offer.include_in_net_cost_f,
    language: offer.language,
    limit_nm: offer.limit_nm,
    limit: offer.limit,
    min_qty_dollar_f: offer.min_qty_dollar_f,
    min_qty: offer.min_qty,
    mix_and_match_f: offer.mix_and_match_f,
    must_buy_f: offer.must_buy_f,
    qty_off_total_purchase_f: offer.qty_off_total_purchase_f,
    unit_price: offer.unit_price,
    value_1: offer.value_1,
    value_2: offer.value_2,
    value_3: offer.value_3,
    reg_price: offer.reg_price,
    reg_price2_d: offer.reg_price2_d,
    liner_f: offer.liner_f,
  });

// True when the list contains two offers with identical entry fields
// (ignores order_num/index). For non-split blocks the backend silently drops
// such duplicates, so warn the operator instead of submitting them.
export const checkDupeOfferEntry = (offersData = []) => {
  const seen = new Set();
  for (const offer of offersData) {
    const key = offerEntryKey(offer);
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
};

// Highest number of identical offer entries in the list. Split blocks allow
// duplicates by design, but a flood of identical rows is accidental
// duplication (e.g. a held clone hotkey).
export const maxIdenticalOfferEntries = (offersData = []) => {
  const counts = {};
  offersData.forEach((offer) => {
    const key = offerEntryKey(offer);
    counts[key] = (counts[key] || 0) + 1;
  });
  const values = Object.values(counts);
  return values.length ? Math.max(...values) : 0;
};

export const incentiveStringEval = (incentive = {}, index = 0, displayOnly = false) => {
  const isValid = incentive.isValid;
  const isGroup = incentive.isGroup ? "Group - " : "";
  const incentive_text = incentive.incentive_text;
  const incentive_type = incentive.incentive_type || "";
  const incentive_attach_to = incentive.incentive_attach_to;
  const incentive_liner_f = incentive.incentive_liner_f ? "(L)" : "";
  const incentive_limit = (incentive.incentive_limit || "");
  const incentive_include_net_cost = incentive.incentive_include_net_cost ? " (Net Cost)" : "";
  const incentive_by_mail = incentive.incentive_by_mail ? " (1)" : " (0)";
  const incentive_online_incentive = incentive.incentive_online_incentive ? " (1)" : " (0)";

  // WIP: how group incentives are displayed
  return (
    <div className="flex-1">
      {isGroup}
      {isGroup ? `(${incentive_liner_f} ${incentive_type})` : `${incentive_liner_f} ${incentive_attach_to} - (${incentive_type})`}
      - {incentive_text} {incentive_limit} {incentive_include_net_cost}
      {incentive_by_mail} {incentive_online_incentive}
    </div>
  );
};

export const parseOffer = ({
  offerdata = [], 
  isExisting = false, 
  isGroupOffer = false, 
  offersLength=0
}) => {
  if (isGroupOffer) {
    const parsedOffers = offerdata.map((offer, index) => {
      const newOffer = {
        ...OFFER_INIT_INFO,
        index: offersLength + index,
        isValid: true
      };
      let offer_text = extractTextBetweenFirstBalancedParenthesis(offer);
      let offer_type = 'Group'; // Temporary for existing Offer Text
      if (offer_text) {
        let index = offer_text.indexOf(" - ")
        offer_type = offer_text.slice(0, index).trim();
        offer_text = offer_text.slice(index + 2).trim();
        let reg_price = offer.match(/\(RPS:(.+?)\)/);
        reg_price = Array.isArray(reg_price) ? reg_price[1] : ''
        let reg_price2_d = offer.match(/\(RPS2:(.+?)\)/);
        reg_price2_d = Array.isArray(reg_price2_d) ? reg_price2_d[1] : ''
        let limit = offer.match(/\(Limit:(.+?)\)/);
        limit = Array.isArray(limit) ? limit[1] : ''
        let min_qty_dollar_f = offer.match(/\(MinQty:(.+?)\)/) ? true : false;
        let min_qty = min_qty_dollar_f ? offer.match(/\(MinQty:(.+?)\)/) : null;
        min_qty = Array.isArray(min_qty) ? min_qty[1] : ''
        let must_buy_f = offer.match(/\(MustBuy\)/) ? true : false;
        let mix_and_match_f = offer.match(/\(MixAndMatch\)/) ? true : false;
        let include_in_net_cost_f = offer.match(/\(NetCost\)/) ? true : false;
        let liner_f = offer.match(/\(Liner\)/) ? true : false;
        newOffer.reg_price = reg_price;
        newOffer.reg_price2_d = reg_price2_d;
        newOffer.limit = limit;
        newOffer.min_qty_dollar_f = min_qty_dollar_f
        newOffer.min_qty = min_qty
        newOffer.must_buy_f = must_buy_f;
        newOffer.mix_and_match_f = mix_and_match_f;
        newOffer.include_in_net_cost_f = include_in_net_cost_f;
        newOffer.liner_f = liner_f;
      } else {
        offer_text = offer;
      }
      newOffer.offer_text = offer_text;
      newOffer.offer_type = offer_type;
      return newOffer;
    })
    return parsedOffers
  } else {
    const parsedOffers = offerdata.map((offer, index) => ({
      ...offer,
      index: offersLength + index,
      isValid: validateOffer(offer),
      existingOffer: isExisting,
    }));
    return parsedOffers;
  }
};

export const parseIncentive = (incentivedata = [], attached_to = 'Adblock', totalOfferIncentive = 0, isGroup = false) => {
  const parsedIncentives = incentivedata.map((incentive, index) => ({
    ...incentive,
    index: totalOfferIncentive + index,
    isValid: validateIncentive(incentive), // all incentives are valid as existing
    incentive_attach_to: attached_to,
    incentive_type: incentive.incentive_type,
    incentive_text: incentive.incentive_text,
    incentive_limit: incentive.limit,
    incentive_include_net_cost: incentive.net_cost,
    incentive_by_mail: incentive.by_mail,
    incentive_online_incentive: incentive.online_incentive,
    incentive_liner_f: incentive.incentive_liner_f,
    isGroup: isGroup,
  }));
  return parsedIncentives;
}

export const calculateNetCost = async ({
  offers = [],
  setNetUnitPriceValue = () => { },
  setShowNetUnitPriceModal = () => { },
  setIsSubmitting = () => { }
}) => {
  // Calculate Net Unit Price
  let get_offer_values = await getOfferValuesApi(offers);
  let calculatedValues = {};
  if (get_offer_values.success) {
    let offerValues = get_offer_values.payload.map((item, index) => {
      let offer = offers.find(offer => offer.offer_text === item.offer_text);
      let min_qty = offer.min_qty;
      let min_qty_dollar_f = offer.min_qty_dollar_f;
      if (min_qty === null) {
        min_qty = 0;
      }
      if (min_qty_dollar_f === null) {
        min_qty_dollar_f = false;
      }
      return {
        ...item,
        min_qty: min_qty,
        min_qty_dollar_f: min_qty_dollar_f,
        liner_f: offer.liner_f,
      };
    });

    // Calculate net unit price for liner_f offers separately
    let linerOffers = offerValues.filter(offer => offer.liner_f === true);
    let nonLinerOffers = offerValues.filter(offer => offer.liner_f === false);

    // Process non-liner offers first if nonLinerOffers length is greater than 1
    if (nonLinerOffers.length > 1) {
      const nonLinerVal = processOffers(nonLinerOffers);
      if (nonLinerVal !== undefined && nonLinerVal !== null) {
        calculatedValues.nonLiner = nonLinerVal;
      }
    }

    // Process liner offers if they exist
    if (linerOffers.length > 1) {
      const linerVal = processOffers(linerOffers);
      if (linerVal !== undefined && linerVal !== null) {
        calculatedValues.liner = linerVal;
      }
    }

    // Only show modal and return false if we have valid calculated values
    if (Object.keys(calculatedValues).length > 0) {
      setNetUnitPriceValue(calculatedValues);
      setShowNetUnitPriceModal(true);
      setIsSubmitting(false);
      return {
        success: false,
        message: "Net cost calculation failed",
        values: calculatedValues
      };
    }
  }
  return {
    success: true,
    message: "Net cost calculation succeeded",
    values: calculatedValues
  };
}

export const validatePrice = ({
  offer_data = [],
  products_data = []
}) => {
  function validate({ offer = {}, products = [], type = "" }) {
    if (_.isEmpty(offer) || products.length === 0) {
      return { isValid: true, reason: null }
    }

    // Parse the offer price range
    const parts = offer.offer_text.split("-");
    const start = parseFloat(parts[0].replace(/[^0-9\.]+/g, ""));
    const end = parts[1] ? parseFloat(parts[1].replace(/[^0-9\.]+/g, "")) : null;

    if (isNaN(start)) {
      return {
        isValid: false,
        reason: `Invalid price format in ${type} offer: "${offer.offer_text}"`
      }
    }

    // If end is provided but invalid, return error
    if (parts[1] && isNaN(end)) {
      return {
        isValid: false,
        reason: `Invalid end price format in ${type} offer: "${offer.offer_text}"`
      }
    }

    // Check if the offer is valid for ALL products of this liner type
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      const limit = product?.brand?.offer_limits;

      if (_.isEmpty(limit)) {
        continue;
      }

      const minLimit = parseFloat(limit.min_n);
      const maxLimit = parseFloat(limit.max_n);

      if (isNaN(minLimit) || isNaN(maxLimit)) {
        return {
          isValid: false,
          reason: `Invalid limit values for ${type} product: ${product.brand?.brand_name || 'Unknown brand'}`
        }
      }

      // Check if start price and end price (if exists) are within the product's brand limits
      const startValid = start >= minLimit && start <= maxLimit;
      const endValid = end === null || (end >= minLimit && end <= maxLimit);

      if (!startValid || !endValid) {
        const priceRange = end !== null ? `${start}-${end}` : `${start}`;
        return {
          isValid: false,
          reason: `Price ${priceRange} is outside limits (${minLimit}-${maxLimit}) for ${type} product: ${product.brand?.brand_name || 'Unknown brand'}`
        }
      }
    }

    return { isValid: true, reason: null };
  }

  const linerOffer = offer_data.filter(offer => offer.offer_type.toLowerCase().includes('price') && offer.liner_f);
  const linerProduct = products_data.filter(product => product.liner_f);
  const nonLinerOffer = offer_data.filter(offer => offer.offer_type.toLowerCase().includes('price') && !offer.liner_f);
  const nonLinerProduct = products_data.filter(product => !product.liner_f);

  const nonLinerResult = validate({ offer: nonLinerOffer[0], products: nonLinerProduct, type: "non-liner" });
  const linerResult = validate({ offer: linerOffer[0], products: linerProduct, type: "liner" });

  // Return validation result with reason for failure
  if (!nonLinerResult.isValid) {
    return { isValid: false, reason: nonLinerResult.reason };
  }

  if (!linerResult.isValid) {
    return { isValid: false, reason: linerResult.reason };
  }

  return { isValid: true, reason: null };
}

export const extractWPurchaseFields = (item) => {
  if (!item.offer_text) return item;
  const e = item.offer_text;
  const qty_off_total_purchase_f = item.qty_off_total_purchase_f;
  const wPurchaseMatch = e.match(/w\/purchase\/(?:any\s)?(\$\d+(\.\d+)?|\d+)/i);
  if (wPurchaseMatch && wPurchaseMatch[1] && wPurchaseMatch[1].toString().length) {
    return {
      ...item,
      min_qty: wPurchaseMatch[1].replace('$', '') || "",
      min_qty_dollar_f: wPurchaseMatch[1].includes('$') || false,
      qty_off_total_purchase_f,
      offer_text: e
    };
  }
  return item;
}

export const approveOfferRestrictions = ({ offers = [], linerNetCost = 0, nonLinerNetCost = 0 }) => {
  if (offers.length === 0) return {
    error: false,
    message: "No offers available"
  };

  // Only one 'Price Ea' and liner
  const linerPriceEaOffers = offers.filter(o => o.offer_type.toLowerCase() === 'price ea' && o.liner_f);
  if (linerPriceEaOffers.length > 1) return {
    error: true,
    message: "Multiple 'Price ea' offers for liner products are not allowed"
  };

  const nonLinerPriceEaOffers = offers.filter(o => o.offer_type.toLowerCase() === 'price ea' && !o.liner_f);
  if (nonLinerPriceEaOffers.length > 1) return {
    error: true,
    message: "Multiple 'Price ea' offers for non-liner products are not allowed"
  };

  // AD-3535 X/For offers with include_in_net_cost_f checked are allowed alongside Price ea offers.
  // processOffers automatically picks the lowest unit_price X/For, matching PEP behavior.
  // if the net cost > price each - message "Please uncheck 'include in net cost' for the X/For offer"
  // const linerXForOffers = offers.some(o => o.offer_type === 'X For' && o.liner_f && o.include_in_net_cost_f);
  // const linerMaxPriceEa = linerPriceEaOffers.length ? Math.max(...linerPriceEaOffers.map(o => parseFloat(o.offer_text.split('-')[0]?.toString().replace(/[^0-9\.]+/g, "")) || 0)) : 0;
  // if (linerXForOffers && linerNetCost != 0 && linerMaxPriceEa != 0 && (linerNetCost > linerMaxPriceEa)) {
  //   return {
  //     error: true,
  //     message: "Liner: Please uncheck 'include in net cost' for the X/For offer"
  //   };
  // }

  // const nonLinerXForOffers = offers.some(o => o.offer_type === 'X For' && !o.liner_f && o.include_in_net_cost_f);
  // const nonLinerMaxPriceEa = nonLinerPriceEaOffers.length ? Math.max(...nonLinerPriceEaOffers.map(o => parseFloat(o.offer_text.split('-')[0]?.toString().replace(/[^0-9\.]+/g, "")) || 0)) : 0;
  // console.log("nonLinerMaxPriceEa:", nonLinerMaxPriceEa, nonLinerNetCost);
  // if (nonLinerXForOffers && nonLinerNetCost != 0 && nonLinerMaxPriceEa != 0 && (nonLinerNetCost > nonLinerMaxPriceEa)) {
  //   console.log("Non-liner net cost validation failed");
  //   return {
  //     error: true,
  //     message: "NonLiner: Please uncheck 'include in net cost' for the X/For offer"
  //   };
  // }
  return { error: false, message: "" };
}

export const COUPON_REBATE_CONFLICT_MESSAGE =
  "This block is flagged as both Coupon and Rebate. Uncheck one of them before continuing.";

export const hasCouponAndRebate = (adblock) =>
  Boolean(adblock?.coupon_f) && Boolean(adblock?.rebate_f);

export const approveOfferValidations = ({ offers = [], incentives = [], adblock }) => {
  if (hasCouponAndRebate(adblock)) {
    return {
      error: true,
      canOverride: false,
      message: COUPON_REBATE_CONFLICT_MESSAGE
    };
  }

  // Save offers must not have a zero amount (e.g. "save $0.00" or "save 0%").
  const zeroSaveOffer = offers.find((o) => {
    if (o.offer_type?.toLowerCase() !== 'save') return false;
    const text = o.offer_text || '';
    const dollarMatch = text.match(/\$\s*([\d,]+(?:\.\d+)?)/);
    if (dollarMatch && parseFloat(dollarMatch[1].replace(/,/g, '')) === 0) return true;
    const percentMatch = text.match(/([\d,]+(?:\.\d+)?)\s*%/);
    if (percentMatch && parseFloat(percentMatch[1].replace(/,/g, '')) === 0) return true;
    return false;
  });
  if (zeroSaveOffer) {
    return {
      error: true,
      canOverride: false,
      message: "Save offers cannot have a zero amount (e.g. save $0.00 or save 0%). Please enter a valid save amount."
    };
  }

  // if multiple save offers have include in net cost checked for each liner and non-liner, display message like "Please confirm both save offers should apply to the net cost.  If not, please make sure to only have the offer resulting in the lowest net cost checked"
  const linerSaveOffer = offers.filter(o => o.offer_type.toLowerCase() === 'save' && o.liner_f && o.include_in_net_cost_f);
  const nonLinerSaveOffer = offers.filter(o => o.offer_type.toLowerCase() === 'save' && !o.liner_f && o.include_in_net_cost_f);
  if (linerSaveOffer.length > 1 || nonLinerSaveOffer.length > 1) {
    return {
      error: true,
      canOverride: true,
      message: "Please confirm both save offers should apply to the net cost. If not, please make sure to only have the offer resulting in the lowest net cost checked"
    };
  }

  // if multiple X/For offers have include in net cost checked, display message "Please remove 'include in net cost' check from all x/for offers except the offer resulting in the lowest net unit price"
  const linerXForOffer = offers.filter(o => o.offer_type.toLowerCase() === 'x for' && o.liner_f && o.include_in_net_cost_f);
  const nonLinerXForOffer = offers.filter(o => o.offer_type.toLowerCase() === 'x for' && !o.liner_f && o.include_in_net_cost_f);
  if (linerXForOffer.length > 1 || nonLinerXForOffer.length > 1) {
    return {
      error: true,
      canOverride: true,
      message: "Please remove 'include in net cost' check from all x/for offers except the offer resulting in the lowest net unit price"
    };
  }

  // If there is a coupon amount, AND a save offer w/ a $ amount, validate that both values are the same. If multiple save offers, use whichever one has “include in net cost” checked. PEP-5737
  const couponAmount = adblock.coupon_amnt;
  // Create deep copies to avoid mutating the original offers
  const linerSaveOffersWithAmount = offers
    .filter(o => o.offer_type.toLowerCase() === 'save' && o.liner_f && o.include_in_net_cost_f)
    .map(o => ({ ...o }));
  const nonLinerSaveOffersWithAmount = offers
    .filter(o => o.offer_type.toLowerCase() === 'save' && !o.liner_f && o.include_in_net_cost_f)
    .map(o => ({ ...o }));

  const saveAmountRegex = /^save\s+\$([\d,]+(?:\.\d+)?)/i;
  function extractAndAssignSaveAmount(offersArr, regex) {
    offersArr.forEach(offer => {
      // remove 'an extra' or 'up to' from offer_text to extract Save amount correctly
      offer.offer_text = offer.offer_text.replace(/an extra|up to/i, '').trim();
      
      // Try to extract dollar amount first
      const match = offer.offer_text.match(regex);
      if (match && match[1]) {
        offer.value_1 = match[1].replace(/,/g, '');
      } else if (offer.offer_text.includes('%')) {
        // Only reset value_1 if no dollar amount found and it's percentage-based
        console.log("Percentage-based save offer detected (no dollar amount), skipping dollar amount extraction.");
        offer.value_1 = "";
      }
    });
  }
  extractAndAssignSaveAmount(linerSaveOffersWithAmount, saveAmountRegex);
  extractAndAssignSaveAmount(nonLinerSaveOffersWithAmount, saveAmountRegex);
  // match coupon amount with offer.value_1 in both liner/non-liner save offers
  if (couponAmount) {
    if (linerSaveOffersWithAmount.length > 0) {
      if (Number(couponAmount) !== Number(linerSaveOffersWithAmount[0].value_1)) {
        return {
          error: true,
          canOverride: false,
          message: "Coupon amount must match the save offer amount."
        };
      }
    }
    if (nonLinerSaveOffersWithAmount.length > 0) {
      if (Number(couponAmount) !== Number(nonLinerSaveOffersWithAmount[0].value_1)) {
        return {
          error: true,
          canOverride: false,
          message: "Coupon amount must match the save offer amount."
        };
      }
    }
  }

  // If there is “coupon” in the offer or incentives(Ad), then the coupon flag must be checked.  Conversely, if the coupon flag is checked, there must be an offer with “coupon” in the offer name. PEP-5670
  // New Requirements:
  // 1. coupon flag checked and/or coupon value selected, make sure there is "circle" or "coupon" in the text.
  // 2. if "circle" is in the text, coupon check and coupon value are allowed, but optional.
  // 3. if "coupon" is in the text, coupon check and coupon value are required for save offer
  // 4. if "coupon" is in the text, coupon check is required for non-save offer, coupon value is optional

  const hasCircleInText = offers.some(o => o.offer_text.toLowerCase().includes('circle')) ||
    incentives.some(i => i.incentive_attach_to === 'Adblock' ? i.incentive_text.toLowerCase().includes('circle') : false);

  const hasCouponInText = offers.some(o => o.offer_text.toLowerCase().includes('coupon')) ||
    incentives.some(i => i.incentive_attach_to === 'Adblock' ? i.incentive_text.toLowerCase().includes('coupon') : false);

  const isCouponFlagChecked = adblock.coupon_f;
  const isCouponValueSelected = !!adblock.coupon_amnt;

  // Requirement 1: coupon flag checked and/or coupon value selected, make sure there is "circle" or "coupon" in the text.
  if ((isCouponFlagChecked || isCouponValueSelected) && !hasCircleInText && !hasCouponInText) {
    return {
      error: true,
      canOverride: false,
      message: "Please ensure that the coupon flag is checked or coupon value is selected only if there is an offer with 'coupon' or 'circle' in the offer name."
    };
  }

  // Requirement 2: if "circle" is in the text, coupon check and coupon value are allowed, but optional.
  if (hasCircleInText) {
    return { error: false, message: "", canOverride: false };
  }

  // Requirement 3 & 4: if "coupon" is in the text
  if (hasCouponInText) {
    const hasSaveWithCoupon = offers.some(o => o.offer_type.toLowerCase() === 'save' && o.offer_text.toLowerCase().includes('coupon'));

    if (hasSaveWithCoupon) {
      // Check if the save offer is percentage-based (dd_index = 0 if "%" in offer_text and "$" not in offer_text else 1)
      const saveWithCoupon = offers.find(o => o.offer_type.toLowerCase() === 'save' && o.offer_text.toLowerCase().includes('coupon'));
      const isPercentageSave = saveWithCoupon && saveWithCoupon.offer_text.includes('%') && !saveWithCoupon.offer_text.includes('$');
      
      // coupon check is required, but coupon value is only required for dollar-based save offers
      if (!isCouponFlagChecked) {
        return {
          error: true,
          canOverride: false,
          message: "For Save offers with 'coupon' in text, Coupon Flag is required."
        };
      }
      
      if (!isPercentageSave && !isCouponValueSelected) {
        return {
          error: true,
          canOverride: false,
          message: "For Save offers with 'coupon' in text, both Coupon Flag and Coupon Value are required."
        };
      }
    } else {
      // coupon check is required for non-save offer (or incentive), coupon value is optional
      if (!isCouponFlagChecked) {
        return {
          error: true,
          canOverride: false,
          message: "For offers with 'coupon' in text, Coupon Flag is required."
        };
      }
    }
  }

  return { error: false, message: "", canOverride: false };
}

export const validateLiner = ({
  productData = [],
  incentiveData = [],
  offerData = []
}) => {
  if (productData.length > 0) {
    const hasProductLiner = productData.some(item => item?.liner_f === true);
    const hasProductNonLiner = productData.some(item => item?.liner_f !== true);

    let linerSelected = false;
    let nonLinerSelected = false;
    const filteredIncentiveData = incentiveData.filter(item => item?.incentive_attach_to === 'Adblock');
    if (offerData.length > 0 || filteredIncentiveData.length > 0) {
      const hasOfferLiner = offerData.some(item => item?.liner_f === true);
      const hasOfferNonLiner = offerData.some(item => item?.liner_f === false);
      const hasIncentiveLiner = filteredIncentiveData.some(item => item?.incentive_liner_f === true);
      const hasIncentiveNonLiner = filteredIncentiveData.some(item => item?.incentive_liner_f === false);

      if (hasProductLiner && hasProductNonLiner) { // Mixed case
        linerSelected = !hasOfferLiner && !hasIncentiveLiner;
        nonLinerSelected = !hasOfferNonLiner && !hasIncentiveNonLiner;
      } else if (hasProductLiner) { // All liners
        linerSelected = !(offerData.every(item => item?.liner_f === true) && filteredIncentiveData.every(item => item?.incentive_liner_f === true));
      } else if (hasProductNonLiner) { // All non-liners
        nonLinerSelected = hasOfferLiner || hasIncentiveLiner;
      }
    } else {
      if (hasProductLiner && hasProductNonLiner) {
        linerSelected = true;
        nonLinerSelected = true;
      } else if (hasProductLiner) {
        linerSelected = true;
      } else if (hasProductNonLiner) {
        nonLinerSelected = true;
      }
    }

    const isLinerSelected = linerSelected
    const isNonLinerSelected = nonLinerSelected

     if (isLinerSelected && isNonLinerSelected){
      return {
        success: false,
        message: "OFFERS MUST HAVE AT-LEAST ONE LINER AND ONE NON-LINER"
      }
     }
     else if (isLinerSelected && !isNonLinerSelected) {
       return {
         success: false,
         message: "ALL/ATLEAST(1) OFFER MUST HAVE A LINER"
       }
     }
     else if (isNonLinerSelected && !isLinerSelected) {
       return {
         success: false,
         message: "ALL/ATLEAST(1) OFFERS MUST BE WITHOUT LINER"
       }
     }
     return {
       success: true,
       message: "Validation passed"
     };
   }
}

// Validate if there's a space directly after '/' in offer or incentive text
// Returns error message if found, empty string otherwise
export const checkSpaceAfterSlash = (offerData = [], incentiveData = []) => {
  // Check offers
  for (const offer of offerData) {
    if (offer?.offer_text && /\/\s/.test(offer.offer_text)) {
      return `Space after "/" is not allowed in offer text: "${offer.offer_text}"`;
    }
  }

  // Check incentives
  for (const incentive of incentiveData) {
    if (incentive?.incentive_text && /\/\s/.test(incentive.incentive_text)) {
      return `Space after "/" is not allowed in incentive text: "${incentive.incentive_text}"`;
    }
  }

  return '';
};
